<script lang="ts" setup>
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/20/solid';
import { ref, useTemplateRef } from 'vue';
import { getNearbyProjects, type GetProjectDetailsProjectResponse } from '@/api';
import NearbyProject from '@/views/projects/NearbyProject.vue';
import SectionHeader from '@/components/SectionHeader.vue';
import { useI18n } from 'vue-i18n';
import { useSsgState } from '@/utilities/ssg-state.ts';

const { project } = defineProps<{
  project: GetProjectDetailsProjectResponse;
}>();

const { t } = useI18n();

const nearbyProjectsContainer = useTemplateRef('nearbyProjectsContainer');
const isAtStart = ref(true);
const isAtEnd = ref(false);
const nearbyProjects = ref<number[]>([]);

const fetchNearbyProjects = async (): Promise<number[]> => {
  const fetch = await getNearbyProjects({
    path: {
      projectId: project.id,
    },
  });
  if (fetch.response?.status == 200 && fetch.data) {
    return fetch.data.items;
  } else {
    throw Error(`Fetching search results failed: ${fetch.error}`);
  }
};

const updateScrollIndicators = () => {
  if (nearbyProjectsContainer.value) {
    const container = nearbyProjectsContainer.value;
    isAtStart.value = container.scrollLeft === 0;
    isAtEnd.value = Math.round(container.scrollWidth - container.scrollLeft) === container.clientWidth;
  }
};

const scrollNearbyProjects = (direction: number) => {
  if (nearbyProjectsContainer.value) {
    const scrollAmount = nearbyProjectsContainer.value.offsetWidth * 0.75;
    nearbyProjectsContainer.value.scrollBy({
      left: direction * scrollAmount,
      behavior: 'smooth',
    });
  }
};

const loadNearbyProjects = async () => {
  try {
    nearbyProjects.value = await fetchNearbyProjects();
  } catch (err) {
    console.error(err);
  }
};

useSsgState({
  key: `nearby-projects:${project.id}`,
  refs: { nearbyProjects },
  isLoaded: () => nearbyProjects.value.length > 0,
  load: loadNearbyProjects,
});
</script>

<template>
  <section>
    <SectionHeader>{{ t('views.project.nearby-projects.header') }}</SectionHeader>
    <div class="relative -top-2">
      <div class="no-print absolute top-0 -left-0.5 z-10 flex h-full items-center justify-center bg-linear-to-r from-white to-50% pl-3">
        <button
          :class="['hover:text-ph-green-300 focus:outline-ph-green-600/20 cursor-pointer rounded-md bg-gray-800/20 py-1 focus:outline-2 focus:outline-offset-2', !isAtStart ? 'text-gray-100' : 'hidden']"
          @click="scrollNearbyProjects(-1)"
        >
          <ChevronLeftIcon class="h-10 w-10" />
          <span class="sr-only">{{ t('views.project.nearby-projects.scroll-left') }}</span>
        </button>
      </div>
      <ul
        ref="nearbyProjectsContainer"
        class="flex scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 flex-row gap-2 overflow-x-auto scroll-smooth px-1 pt-1 pb-2 md:scrollbar-none"
        role="list"
        @scroll="updateScrollIndicators"
      >
        <template v-for="(projectId, index) in nearbyProjects" :key="projectId">
          <NearbyProject :index="index" :project-id="projectId" />
        </template>
      </ul>
      <div class="no-print absolute top-0 -right-0.5 z-10 flex h-full items-center justify-center bg-linear-to-l from-white to-50% pr-3">
        <button :class="['hover:text-ph-green-300 focus:outline-ph-green-600/20 cursor-pointer rounded-md bg-gray-800/20 py-1 focus:outline-2 focus:outline-offset-2', !isAtEnd ? 'text-gray-100' : 'hidden']" @click="scrollNearbyProjects(1)">
          <ChevronRightIcon class="h-10 w-10" />
          <span class="sr-only">{{ t('views.project.nearby-projects.scroll-right') }}</span>
        </button>
      </div>
    </div>
  </section>
</template>

<style lang="css" scoped></style>
